/**
 * Billing Entity panel on the retailer detail page.
 *
 * A storefront (retailer_id) is not the party we invoice; the LEGAL ENTITY behind it is,
 * and one entity may own several storefronts (BeOne: be-one.nl + beonefashion.de). This
 * panel edits that entity. It has its own Save, separate from the retailer form above it,
 * because the two go to different rows through different services: one Save button
 * spanning two writers is how partial saves happen.
 *
 * VIES validation goes through handoff-invoicing, which owns the verdict columns; every
 * other field saves through handoff-admin.
 */

import React, { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { HelpOutline as HelpIcon, Save as SaveIcon } from '@mui/icons-material';
import { billingEntityApi, retailerApi } from '../../services/api';
import { vatApi } from '../../services/invoicingApi';
import CountrySelect from '../CountrySelect';

const EMPTY = {
  legal_name: '',
  address: '',
  country_code: 'NL',
  vat_number: '',
  chamber_of_commerce: '',
  billing_email: '',
  billing_cc_emails: [],
  billing_contact_name: '',
  payment_terms_days: 30,
  invoice_language: 'nl',
  currency: 'EUR',
  bank_details: null,
  billing_agreement_signed_at: '',
  billing_agreement_version: '',
  billing_agreement_reference: '',
};

export default function BillingEntityPanel({ retailer, onChanged }) {
  const entity = retailer.billing_entity || null;
  const [mode, setMode] = useState(entity ? 'linked' : 'unlinked'); // linked | unlinked | create
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [vatChecking, setVatChecking] = useState(false);
  const [vatResult, setVatResult] = useState(null);
  const [vatError, setVatError] = useState('');
  const [available, setAvailable] = useState([]);
  const [linkTarget, setLinkTarget] = useState(null);

  useEffect(() => {
    setMode(entity ? 'linked' : 'unlinked');
    setEdits({});
    setVatResult(null);
  }, [entity?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (mode === 'unlinked' && available.length === 0) {
      billingEntityApi.list().then((d) => setAvailable(d?.entities || [])).catch(() => {});
    }
  }, [mode, available.length]);

  const base = mode === 'create' ? EMPTY : entity || EMPTY;
  const value = (key) => {
    const v = key in edits ? edits[key] : base[key];
    return v === null || v === undefined ? '' : v;
  };
  const change = (key, v) => setEdits((prev) => ({ ...prev, [key]: v }));

  const bank = (key) => {
    const current = 'bank_details' in edits ? edits.bank_details : base.bank_details;
    return (current && current[key]) || '';
  };
  const changeBank = (key, v) => {
    const current = ('bank_details' in edits ? edits.bank_details : base.bank_details) || {};
    const next = { ...current, [key]: v || undefined };
    const hasAny = Object.values(next).some((x) => x);
    change('bank_details', hasAny ? next : null);
  };

  // Deliberately NOT on blur: VIES is slow and rate-limited, and firing on blur checks
  // half-typed numbers. In create mode there is no row to hold a verdict yet, so the
  // non-persisting check is used and the response prefills empty fields.
  const validateVat = async () => {
    const vat = value('vat_number');
    if (!vat) return;
    setVatChecking(true);
    setVatError('');
    setVatResult(null);
    try {
      if (mode === 'create') {
        const res = await vatApi.validate(vat);
        setVatResult(res);
        if (res.valid) {
          setEdits((prev) => ({
            ...prev,
            legal_name: prev.legal_name || res.suggested_name || res.trader_name || '',
            address: prev.address || res.suggested_address || '',
            country_code: prev.country_code || res.country_code || 'NL',
          }));
        }
      } else {
        const res = await vatApi.updateEntityVat(entity.id, vat, true);
        setVatResult(res);
        setEdits((prev) => {
          const next = { ...prev };
          delete next.vat_number; // now persisted by the invoicing service
          // Fill what is still empty from the VIES record. Unsaved: the operator checks
          // it and presses Save, which goes through handoff-admin like any other edit.
          if (res.valid) {
            if (!(prev.legal_name || entity.legal_name) && res.trader_name) next.legal_name = res.suggested_name || res.trader_name;
            if (!(prev.address || entity.address) && res.suggested_address) next.address = res.suggested_address;
          }
          return next;
        });
        await onChanged();
      }
    } catch (err) {
      setVatError(err.body?.message || err.body?.error || err.message || 'VIES check failed');
    }
    setVatChecking(false);
  };

  const details = vatResult || entity?.vat_validation_details || null;
  const tidy = (n) => (n || '').trim().replace(/\.$/, '');
  const fieldNote = (kind) => {
    if (!details || !details.valid) return null;
    const map = {
      name: [details.name_mismatch, details.registered_name],
      address: [details.address_mismatch, details.registered_address],
      country: [details.country_mismatch, details.registered_country_code],
    };
    const [mismatch, viesValue] = map[kind] || [];
    if (!mismatch || !viesValue) return null;
    return (
      <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
        VIES has: {tidy(viesValue)}
      </Typography>
    );
  };
  const vatStatus = () => {
    if (vatChecking) return <Chip size="small" label="Checking…" />;
    if (vatError) return <Chip size="small" color="warning" label="Not checked" title={vatError} />;
    if ('vat_number' in edits) return <Chip size="small" label="Unsaved" />;
    if (!base.vat_number) return null;
    if (base.vat_valid === true) return <Chip size="small" color="success" label="Valid" />;
    if (base.vat_valid === false) return <Chip size="small" color="error" label="Invalid" />;
    return <Chip size="small" color="warning" label="Not validated" />;
  };

  const save = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      if (mode === 'create') {
        await billingEntityApi.create({ ...EMPTY, ...edits, storefront_ids: [retailer.retailer_id] });
        setSuccess('Billing entity created and linked');
      } else {
        if (Object.keys(edits).length === 0) return;
        await billingEntityApi.update(entity.id, edits);
        setSuccess('Billing entity updated');
      }
      setEdits({});
      await onChanged();
    } catch (err) {
      setError(err.body?.error || err.message);
    }
    setSaving(false);
  };

  const link = async () => {
    if (!linkTarget) return;
    setSaving(true);
    setError('');
    try {
      await retailerApi.update(retailer.retailer_id, { billing_entity_id: linkTarget.id });
      setSuccess(`Linked to ${linkTarget.legal_name}`);
      await onChanged();
    } catch (err) {
      setError(err.body?.error || err.message);
    }
    setSaving(false);
  };

  const signed = !!value('billing_agreement_signed_at');
  const siblings = (entity?.storefronts || []).filter((s) => s !== retailer.retailer_id);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
        <Typography variant="body2" fontWeight={600}>Billing Entity</Typography>
        <Tooltip
          title="The legal entity this storefront bills through. Invoices, self-bills and settlements are issued per entity, covering all of its storefronts. A storefront without an entity is skipped by invoice runs (missing_billing_info); an entity without a signed billing agreement is skipped in both directions (billing_agreement_missing)."
          arrow
        >
          <HelpIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
        </Tooltip>
        {entity && (
          <Chip
            size="small"
            sx={{ ml: 1 }}
            color={signed ? 'success' : 'warning'}
            label={signed ? 'Agreement signed' : 'Agreement not signed'}
          />
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {mode === 'unlinked' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Alert severity="warning">
            This storefront is not linked to a billing entity, so it cannot be invoiced. Link it to an
            existing legal entity (for a second storefront of the same company) or create one.
          </Alert>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <Autocomplete
              options={available}
              getOptionLabel={(e) => `${e.legal_name}${e.storefronts?.length ? ` (${e.storefronts.join(', ')})` : ''}`}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              value={linkTarget}
              onChange={(ev, v) => setLinkTarget(v)}
              renderInput={(params) => <TextField {...params} size="small" label="Link to existing entity" />}
              sx={{ flex: 1 }}
            />
            <Button variant="outlined" onClick={link} disabled={!linkTarget || saving}>Link</Button>
            <Button variant="contained" onClick={() => setMode('create')} disabled={saving}>Create entity</Button>
          </Box>
        </Box>
      )}

      {mode !== 'unlinked' && (
        <>
          {siblings.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Also bills: {siblings.join(', ')}. Changes here apply to those storefronts too.
            </Typography>
          )}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <TextField
                  size="small"
                  label="VAT Number"
                  value={value('vat_number')}
                  onChange={(e) => change('vat_number', (e.target.value || '').toUpperCase() || null)}
                  fullWidth
                />
                <Button
                  size="small"
                  variant="outlined"
                  onClick={validateVat}
                  disabled={vatChecking || !value('vat_number')}
                  sx={{ flexShrink: 0, mt: 0.25 }}
                >
                  {vatChecking ? 'Checking…' : 'Validate'}
                </Button>
                <Box sx={{ pt: 0.75, flexShrink: 0 }}>{vatStatus()}</Box>
              </Box>
              {vatError && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Could not reach the VAT check: {vatError}. The number will still be saved.
                </Typography>
              )}
              {!vatError && details?.name_mismatch && details.registered_name && (
                <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
                  This VAT number is registered to {tidy(details.registered_name)}.
                </Typography>
              )}
              {!vatError && !details?.name_mismatch && details?.valid && details.trader_name && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Registered to {tidy(details.trader_name)}.
                </Typography>
              )}
            </Box>
            <Box>
              <TextField
                size="small"
                label="Legal Name"
                value={value('legal_name')}
                onChange={(e) => change('legal_name', e.target.value)}
                required
                fullWidth
              />
              {fieldNote('name')}
            </Box>
            <TextField
              size="small"
              label="Chamber of Commerce (KvK)"
              value={value('chamber_of_commerce')}
              onChange={(e) => change('chamber_of_commerce', e.target.value || null)}
              helperText="Company register number."
              fullWidth
            />
            <TextField
              size="small"
              label="Billing Contact Name (optional)"
              value={value('billing_contact_name')}
              onChange={(e) => change('billing_contact_name', e.target.value || null)}
              fullWidth
            />
            <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
              <TextField
                size="small"
                label="Address"
                value={value('address')}
                onChange={(e) => change('address', e.target.value)}
                multiline
                minRows={2}
                required
                fullWidth
              />
              {fieldNote('address')}
            </Box>
            <TextField
              size="small"
              label="Billing Email"
              value={value('billing_email')}
              onChange={(e) => change('billing_email', e.target.value)}
              required
              fullWidth
            />
            <TextField
              size="small"
              label="CC on billing email"
              value={Array.isArray(value('billing_cc_emails')) ? value('billing_cc_emails').join(', ') : (value('billing_cc_emails') || '')}
              onChange={(e) => change('billing_cc_emails', e.target.value)}
              helperText="Copied on every statement email. Separate addresses with commas."
              fullWidth
            />
            <Box>
              <CountrySelect
                label="Billing Country"
                value={value('country_code')}
                onChange={(code) => change('country_code', code)}
                helperText="Drives VAT treatment and date/number formats."
                fullWidth
              />
              {fieldNote('country')}
            </Box>
            <TextField
              size="small"
              label="Payment Terms (days)"
              type="number"
              value={value('payment_terms_days')}
              onChange={(e) => change('payment_terms_days', parseInt(e.target.value, 10) || '')}
              inputProps={{ min: 1, max: 365 }}
              helperText="Applies to whichever party is net debtor."
              fullWidth
            />
            <TextField
              select
              size="small"
              label="Invoice Language"
              value={value('invoice_language') || 'nl'}
              onChange={(e) => change('invoice_language', e.target.value)}
              helperText="Words only; formats follow the billing country."
              fullWidth
            >
              <MenuItem value="nl">Nederlands</MenuItem>
              <MenuItem value="en">English</MenuItem>
            </TextField>
          </Box>

          <Typography variant="body2" fontWeight={600} sx={{ mt: 3, mb: 1 }}>Bank Account</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Where a net amount owed to this entity is paid. Account holder is asked for separately from the
            legal name because banks reject transfers whose name does not match the account.
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <TextField
              size="small"
              label="Account Holder"
              value={bank('account_holder')}
              onChange={(e) => changeBank('account_holder', e.target.value)}
              fullWidth
            />
            <TextField
              size="small"
              label="IBAN"
              value={bank('iban')}
              onChange={(e) => changeBank('iban', (e.target.value || '').toUpperCase())}
              fullWidth
            />
            <TextField
              size="small"
              label="BIC (optional)"
              value={bank('bic')}
              onChange={(e) => changeBank('bic', (e.target.value || '').toUpperCase())}
              helperText="8 or 11 characters."
              fullWidth
            />
          </Box>

          <Typography variant="body2" fontWeight={600} sx={{ mt: 3, mb: 1 }}>Billing Agreement</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            The factureringsovereenkomst (self-billing, set-off, 14-day acceptance, payment term). Until it is
            signed nothing is billed in either direction. Version and reference are on the document header.
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
            <TextField
              size="small"
              label="Signed on"
              type="date"
              value={(value('billing_agreement_signed_at') || '').slice(0, 10)}
              onChange={(e) => change('billing_agreement_signed_at', e.target.value || null)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              size="small"
              label="Version"
              value={value('billing_agreement_version')}
              onChange={(e) => change('billing_agreement_version', e.target.value || null)}
              placeholder="1.0 (september 2026)"
              fullWidth
            />
            <TextField
              size="small"
              label="Reference (Kenmerk)"
              value={value('billing_agreement_reference')}
              onChange={(e) => change('billing_agreement_reference', e.target.value || null)}
              placeholder="HO-BA-2026-…"
              fullWidth
            />
          </Box>

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            {mode === 'create' && (
              <Button onClick={() => { setMode('unlinked'); setEdits({}); }} disabled={saving}>Cancel</Button>
            )}
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={save}
              disabled={saving || (mode === 'linked' && Object.keys(edits).length === 0)}
            >
              {saving ? 'Saving...' : mode === 'create' ? 'Create Billing Entity' : 'Save Billing Entity'}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
}
